import { useI18n } from '@/i18n/i18n-context'
import { LANGUAGE_NAMES, SUPPORTED_LANGUAGES, type SupportedLanguage } from '@/i18n/translations'
import { cn } from '@/lib/cn'
import { Button } from '@/ui/button'
import { Card } from '@/ui/card'

interface Props {
  readonly onStart: () => void
  /**
   * The patient choosing a language for themselves, which overrides the one they were enrolled in.
   *
   * HERE AND NOWHERE ELSE, because this is the first screen of a scan link and the last one before
   * the camera: a picker on every screen would be a control the patient meets while holding a phone
   * to their face, and one after the scan would be an offer to re-read a page they have finished.
   */
  readonly onLanguageChange: (language: SupportedLanguage) => void
}

/**
 * The last screen before the camera, and the only thing it asks for is the tap.
 *
 * It asked for height and weight until docs/decisions.md D-15. Nothing here needs them - the Mindset
 * SDK is given `{age, gender}` and no more - and what arrived was a patient's estimate of their own
 * body, typed on a phone.
 *
 * **The button is not ceremony.** `getUserMedia` and `video.play()` need a user gesture: iOS Safari
 * rejects a play() no tap preceded, and every mobile browser raises the camera-permission dialog the
 * instant the call is made. Without a screen here, opening the link would put that dialog in front
 * of a patient who has not been told what the page is for - which is both worse UX and a worse
 * consent story. So the form went and the gesture stayed, and the space it left is where the page
 * says what is about to happen.
 *
 * The wording is the product owner's, verbatim (docs/decisions.md D-49). The privacy line it makes
 * is one this app can be held to: both vendors' engines are WebAssembly on the device, and the
 * page's own Content-Security-Policy admits `connect-src` for this service and the licence endpoint
 * alone, so there is nowhere for a frame to go.
 */
export function StartScreen({ onStart, onLanguageChange }: Props) {
  const { t, language } = useI18n()

  return (
    <Card className="flex flex-col gap-5">
      {/*
        * The enrolled language is a good guess and not a fact: a record says what somebody was
        * registered as speaking, which is not always what they read. So it is a default, and this
        * changes it.
        */}
      <div className="flex justify-end">
        <div
          role="group"
          aria-label={t('languageLabel')}
          className="inline-flex rounded-lg border border-slate-200 p-0.5"
        >
          {SUPPORTED_LANGUAGES.map((code) => (
            <button
              key={code}
              type="button"
              onClick={() => onLanguageChange(code)}
              aria-pressed={code === language}
              className={cn(
                'rounded-md px-3 py-1 text-sm transition-colors',
                code === language ? 'bg-slate-900 text-slate-50' : 'text-slate-600',
              )}
            >
              {LANGUAGE_NAMES[code]}
            </button>
          ))}
        </div>
      </div>

      <h1 className="text-lg font-semibold text-slate-900">{t('startPrompt')}</h1>

      <ol className="flex list-decimal flex-col gap-2 pl-5 text-sm text-slate-700">
        <li>{t('startStepSit')}</li>
        <li>{t('startStepHold')}</li>
        <li>{t('startStepCamera')}</li>
        <li>{t('startStepStill')}</li>
      </ol>

      <p className="text-sm text-slate-600">{t('startPrivacy')}</p>

      <Button type="button" onClick={onStart}>
        {t('startScan')}
      </Button>
    </Card>
  )
}
